import React, { useMemo, useState } from "react";
import { Box } from "@chakra-ui/react";
import { LandingTheme } from "../../landingThemes";
import { useLandingTheme } from "../../LandingThemeContext";
import { useLandingCopy } from "../../LandingLanguageContext";
import { QualityChartMetricKey } from "../../landingCopy";

// Scores as shown on the Framer page; series order is Jungle then 4 competitors. The series and
// metric names are in landingCopy.ts (qualityChart), in the same order.
const JUNGLE_SERIES_INDEX = 0;

export type ScoreGroup = { metricKey: QualityChartMetricKey; values: number[] };

export const SCORE_GROUPS_IN_FRAMER_ORDER: ScoreGroup[] = [
  { metricKey: "relevanceToYourDocument", values: [99, 93, 78, 99, 33] },
  { metricKey: "easyToUnderstand", values: [94, 92, 85, 88, 89] },
  { metricKey: "plausibleWrongAnswerChoices", values: [69, 55, 53, 28, 56] },
];

const Y_AXIS_TICKS = [100, 75, 50, 25, 0];

// Hovering a bar highlights that competitor across all groups (Jungle always
// stays highlighted) and fades the rest to this opacity.
const FADED_BAR_OPACITY = 0.22;

// Ported from the Framer chart's stylesheet (class prefix "sbc"), including its
// tablet (≤900px) tweaks and the phone (≤640px) switch to horizontal bars.
function buildChartStyles(theme: LandingTheme, variant: "original" | "themed") {
  const legendText = theme.isDark ? theme.color.text : "#454A46";
  const isThemed = variant === "themed";
  return {
    "& .sbc-chart": { display: "flex", gap: "12px", marginTop: "12px" },
    "& .sbc-y-axis": { position: "relative", width: "32px", height: "320px", flexShrink: 0 },
    "& .sbc-y-label": {
      position: "absolute",
      right: "4px",
      transform: "translateY(-50%)",
      fontSize: isThemed ? "12px" : "11px",
      fontWeight: 500,
      color: theme.color.muted,
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1,
    },
    "& .sbc-plot": { flex: 1, position: "relative", minWidth: 0 },
    "& .sbc-gridlines": { position: "absolute", top: 0, left: 0, right: 0, height: "320px", pointerEvents: "none" },
    "& .sbc-gridline": { position: "absolute", left: 0, right: 0, height: 0, borderTop: `1px dashed ${theme.chart.gridline}` },
    "& .sbc-baseline": { borderTop: `1px solid ${theme.chart.baseline}` },
    "& .sbc-groups": { position: "relative", display: "flex", gap: "32px" },
    "& .sbc-group": { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },
    "& .sbc-bars": {
      height: "320px",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      gap: "4px",
      padding: "0 4px",
    },
    "& .sbc-bar": {
      flex: 1,
      maxWidth: "36px",
      borderRadius: "4px 4px 0 0",
      position: "relative",
      minHeight: "4px",
      transition: "opacity 0.22s ease",
      cursor: "default",
    },
    "& .sbc-score-label": {
      position: "absolute",
      bottom: "100%",
      left: "50%",
      transform: "translateX(-50%)",
      marginBottom: "7px",
      fontSize: isThemed ? "13px" : "16px",
      fontWeight: isThemed ? 500 : 600,
      color: isThemed ? theme.color.muted : theme.color.text,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
    "& .sbc-bar-jungle .sbc-score-label": { color: theme.chart.jungle, fontWeight: 700, fontSize: "16px" },
    "& .sbc-group-label": {
      marginTop: "16px",
      textAlign: "center",
      fontSize: "15px",
      fontWeight: 600,
      color: theme.color.text,
      letterSpacing: "0.01em",
      padding: "0 4px",
      lineHeight: 1.3,
    },
    "& .sbc-legend": {
      display: "flex",
      flexWrap: "wrap",
      gap: "6px 18px",
      alignItems: "center",
      justifyContent: "center",
      marginTop: "24px",
      padding: "14px 4px 0",
      borderTop: `1px solid ${theme.chart.gridline}`,
    },
    "& .sbc-legend-item": { display: "flex", alignItems: "center", gap: "7px", fontSize: isThemed ? "13px" : "12px", fontWeight: 500, color: legendText },
    "& .sbc-legend-item-jungle": { fontWeight: 700, color: theme.isDark ? theme.chart.jungle : theme.color.accentDark },
    "& .sbc-swatch": { width: "11px", height: "11px", borderRadius: "2px", flexShrink: 0 },
    "& .sbc-mobile": { display: "none" },

    "@media (max-width: 900px)": {
      "& .sbc-y-axis, & .sbc-gridlines": { height: "280px" },
      "& .sbc-bars": { height: "280px", gap: "3px", padding: "0 2px" },
      "& .sbc-bar": { maxWidth: "28px" },
      "& .sbc-groups": { gap: "18px" },
      "& .sbc-score-label": { fontSize: "13px", marginBottom: "5px" },
      "& .sbc-group-label": { fontSize: "13px", marginTop: "12px" },
      "& .sbc-legend": { gap: "6px 14px" },
    },

    "@media (max-width: 640px)": {
      "& .sbc-chart": { display: "none" },
      "& .sbc-legend": { gap: "6px 12px", paddingTop: "12px", marginTop: "16px" },
      "& .sbc-swatch": { width: "10px", height: "10px" },
      "& .sbc-mobile": { display: "flex", flexDirection: "column", gap: "10px", marginTop: "4px" },
      "& .sbc-mgroup": { border: theme.card.border, borderRadius: "10px", padding: "10px 12px", background: theme.color.surface },
      "& .sbc-mgroup-title": { fontSize: "13px", fontWeight: 600, color: theme.color.text, letterSpacing: "0.02em", marginBottom: "8px" },
      "& .sbc-mbars": { display: "flex", flexDirection: "column", gap: "4px" },
      // 78px fits the English series names. A translated name that is a little wider ("Competidor 1")
      // widens its own row's label column instead of being cut to an ellipsis, which would hide its number.
      "& .sbc-mrow": { display: "grid", gridTemplateColumns: "minmax(78px, max-content) 1fr 46px", alignItems: "center", gap: "10px" },
      "& .sbc-mlabel": { fontSize: "12px", fontWeight: 500, color: legendText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
      "& .sbc-mlabel-jungle": { fontWeight: 700, color: theme.isDark ? theme.chart.jungle : theme.color.accentDark },
      "& .sbc-mbar-track": { width: "100%", height: "14px", background: theme.chart.trackBg, borderRadius: "4px", overflow: "hidden" },
      "& .sbc-mbar": { height: "100%", borderRadius: "4px", transition: "width 0.3s ease" },
      "& .sbc-mscore": { fontSize: "14px", fontWeight: 600, color: theme.color.text, fontVariantNumeric: "tabular-nums", textAlign: "right" },
    },
  } as const;
}

export function ScoreBarChart({
  groups,
  variant,
  // Themed versions draw every competitor in one tone and collapse the legend to Jungle vs competitors.
  singleCompetitorTone = false,
}: {
  groups: ScoreGroup[];
  variant: "original" | "themed";
  singleCompetitorTone?: boolean;
}) {
  const theme = useLandingTheme();
  const qualityChartCopy = useLandingCopy().qualityChart;
  const seriesNames = qualityChartCopy.seriesNames;
  const chartStyles = useMemo(() => buildChartStyles(theme, variant), [theme, variant]);
  const competitorColors = singleCompetitorTone
    ? [theme.chart.competitors[1], theme.chart.competitors[1], theme.chart.competitors[1], theme.chart.competitors[1]]
    : theme.chart.competitors;
  const seriesColors = [theme.chart.jungle, ...competitorColors];
  const [hoveredSeriesIndex, setHoveredSeriesIndex] = useState<number | null>(null);

  const opacityForSeries = (seriesIndex: number) => {
    const nothingIsHovered = hoveredSeriesIndex === null;
    const isHighlightedSeries = seriesIndex === hoveredSeriesIndex || seriesIndex === JUNGLE_SERIES_INDEX;
    return nothingIsHovered || isHighlightedSeries ? 1 : FADED_BAR_OPACITY;
  };

  const legendEntries = singleCompetitorTone
    ? [
        { name: seriesNames[JUNGLE_SERIES_INDEX], color: theme.chart.jungle, isJungle: true },
        { name: qualityChartCopy.singleToneCompetitorsLegend, color: competitorColors[0], isJungle: false },
      ]
    : seriesNames.map((seriesName, seriesIndex) => ({ name: seriesName, color: seriesColors[seriesIndex], isJungle: seriesIndex === JUNGLE_SERIES_INDEX }));

  return (
    <Box fontFamily={theme.font.body} sx={chartStyles}>
      {/* Desktop / tablet: vertical grouped bars */}
      <div className="sbc-chart">
        <div className="sbc-y-axis">
          {Y_AXIS_TICKS.map((tickValue) => (
            <span key={tickValue} className="sbc-y-label" style={{ top: `${100 - tickValue}%` }}>
              {tickValue}
            </span>
          ))}
        </div>
        <div className="sbc-plot">
          <div className="sbc-gridlines">
            {Y_AXIS_TICKS.map((tickValue) => (
              <div key={tickValue} className={tickValue === 0 ? "sbc-gridline sbc-baseline" : "sbc-gridline"} style={{ top: `${100 - tickValue}%` }} />
            ))}
          </div>
          <div className="sbc-groups">
            {groups.map((scoreGroup) => (
              <div key={scoreGroup.metricKey} className="sbc-group">
                <div className="sbc-bars" onMouseLeave={() => setHoveredSeriesIndex(null)}>
                  {scoreGroup.values.map((value, seriesIndex) => {
                    const isJungle = seriesIndex === JUNGLE_SERIES_INDEX;
                    return (
                      <div
                        key={seriesNames[seriesIndex]}
                        className={isJungle ? "sbc-bar sbc-bar-jungle" : "sbc-bar"}
                        style={{ height: `${value}%`, background: seriesColors[seriesIndex], opacity: opacityForSeries(seriesIndex) }}
                        onMouseEnter={() => setHoveredSeriesIndex(seriesIndex)}
                      >
                        <span className="sbc-score-label">{value}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="sbc-group-label">{qualityChartCopy.metricNames[scoreGroup.metricKey]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Phone: one card per group with horizontal bars */}
      <div className="sbc-mobile">
        {groups.map((scoreGroup) => (
          <div key={scoreGroup.metricKey} className="sbc-mgroup">
            <div className="sbc-mgroup-title">{qualityChartCopy.metricNames[scoreGroup.metricKey]}</div>
            <div className="sbc-mbars">
              {scoreGroup.values.map((value, seriesIndex) => {
                const isJungle = seriesIndex === JUNGLE_SERIES_INDEX;
                return (
                  <div key={seriesNames[seriesIndex]} className="sbc-mrow">
                    <div className={isJungle ? "sbc-mlabel sbc-mlabel-jungle" : "sbc-mlabel"}>{seriesNames[seriesIndex]}</div>
                    <div className="sbc-mbar-track">
                      <div className="sbc-mbar" style={{ width: `${value}%`, background: seriesColors[seriesIndex] }} />
                    </div>
                    <div className="sbc-mscore">{value}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="sbc-legend">
        {legendEntries.map((legendEntry) => (
          <div key={legendEntry.name} className={legendEntry.isJungle ? "sbc-legend-item sbc-legend-item-jungle" : "sbc-legend-item"}>
            <span className="sbc-swatch" style={{ background: legendEntry.color }} />
            {legendEntry.name}
          </div>
        ))}
      </div>
    </Box>
  );
}
